import { template } from "@ember/template-compiler";
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { schedule } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked, cached } from '@glimmer/tracking';
import { enqueueTask, restartableTask, timeout, all, task } from 'ember-concurrency';
import perform from 'ember-concurrency/helpers/perform';
import max from 'lodash/max';
import { MatrixEvent } from 'matrix-js-sdk';
import pluralize from 'pluralize';
import { TrackedObject } from 'tracked-built-ins';
import { v4 as uuidv4 } from 'uuid';
import { BoxelButton, BoxelSelect } from '@cardstack/boxel-ui/components';
import { eq, not } from '@cardstack/boxel-ui/helpers';
import { CheckMark } from '@cardstack/boxel-ui/icons';
import { unixTime } from '@cardstack/runtime-common';
import { DEFAULT_LLM_LIST } from '@cardstack/runtime-common/matrix-constants';
import AddSkillsToRoomCommand from '@cardstack/host/commands/add-skills-to-room';
import UpdateSkillActivationCommand from '@cardstack/host/commands/update-skill-activation';
import { Message } from '@cardstack/host/lib/matrix-classes/message';
import type { StackItem } from '@cardstack/host/lib/stack-item';
import { getAutoAttachment } from '@cardstack/host/resources/auto-attached-card';
import { getRoom } from '@cardstack/host/resources/room';
import type CardService from '@cardstack/host/services/card-service';
import type CommandService from '@cardstack/host/services/command-service';
import type MatrixService from '@cardstack/host/services/matrix-service';
import { type MonacoSDK } from '@cardstack/host/services/monaco-service';
import type OperatorModeStateService from '@cardstack/host/services/operator-mode-state-service';
import { type CardDef } from 'https://cardstack.com/base/card-api';
import type { SkillCard } from 'https://cardstack.com/base/skill-card';
import AiAssistantCardPicker from '../ai-assistant/card-picker';
import AiAssistantChatInput from '../ai-assistant/chat-input';
import { AiAssistantConversation } from '../ai-assistant/message';
import NewSession from '../ai-assistant/new-session';
import AiAssistantSkillMenu from '../ai-assistant/skill-menu';
import RoomMessage from './room-message';
import type RoomData from '../../lib/matrix-classes/room';
import type { Skill } from '../ai-assistant/skill-menu';
interface Signature {
    Args: {
        roomId: string;
        monacoSDK: MonacoSDK;
    };
}
export default class Room extends Component<Signature> {
    static{
        template(`
    {{#if (not this.doMatrixEventFlush.isRunning)}}
      <section
        class='room'
        data-room-settled={{this.doWhenRoomChanges.isIdle}}
        data-test-room-settled={{this.doWhenRoomChanges.isIdle}}
        data-test-room-name={{this.roomResource.name}}
        data-test-room={{@roomId}}
      >
        <AiAssistantConversation
          @registerConversationScroller={{this.registerConversationScroller}}
          @setScrollPosition={{this.setScrollPosition}}
        >
          {{#each this.messages as |message i|}}
            <RoomMessage
              @roomId={{@roomId}}
              @message={{message}}
              @index={{i}}
              @registerScroller={{this.registerMessageScroller}}
              @isPending={{this.isPendingMessage message}}
              @monacoSDK={{@monacoSDK}}
              @isStreaming={{this.isMessageStreaming message i}}
              @isDisplayingCode={{this.isDisplayingCode message}}
              @onToggleViewCode={{fn this.toggleViewCode message}}
              @currentEditor={{this.currentMonacoContainer}}
              @setCurrentEditor={{this.setCurrentMonacoContainer}}
              @retryAction={{this.maybeRetryAction i message}}
              data-test-message-idx={{i}}
            />
          {{else}}
            <NewSession @sendPrompt={{this.sendMessage}} />
          {{/each}}
          {{#if this.room}}
            {{#if this.showUnreadIndicator}}
              <div class='unread-indicator'>
                <BoxelButton
                  @size='tall'
                  @kind='primary'
                  class='unread-button'
                  data-test-unread-messages-button
                  {{on 'click' this.scrollToFirstUnread}}
                >{{this.unreadMessageText}}</BoxelButton>
              </div>
            {{else}}
              <AiAssistantSkillMenu
                class='skills'
                @skills={{this.sortedSkills}}
                @onChooseCard={{perform this.attachSkillTask}}
                @onUpdateSkillIsActive={{perform this.updateSkillIsActiveTask}}
                data-test-skill-menu
              />
            {{/if}}
          {{/if}}
        </AiAssistantConversation>

        <footer class='room-actions'>
          <div class='chat-input-area' data-test-chat-input-area>
            <AiAssistantChatInput
              @value={{this.messageToSend}}
              @onInput={{this.setMessage}}
              @onSend={{this.sendMessage}}
              @canSend={{this.canSend}}
              data-test-message-field={{@roomId}}
            />
            <div class='chat-input-area__bottom-section'>
              <AiAssistantCardPicker
                @autoAttachedCards={{this.autoAttachedCards}}
                @cardsToAttach={{this.cardsToAttach}}
                @chooseCard={{this.chooseCard}}
                @removeCard={{this.removeCard}}
              />
              <BoxelSelect
                class='supported-llms'
                @selected={{this.roomResource.activeLLM}}
                @verticalPosition='above'
                @onChange={{this.roomResource.activateLLM}}
                @options={{this.supportedLLMs}}
                @matchTriggerWidth={{false}}
                @disabled={{this.roomResource.isActivatingLLM}}
                @dropdownClass='supported-llms__dropdown'
                as |item|
              >
                <div class='llm'>
                  <div class='check-mark'>
                    {{#if (eq this.roomResource.activeLLM item)}}
                      <CheckMark width='12' height='12' />
                    {{/if}}
                  </div>
                  <span
                    class='llm-name'
                    data-test-llm-name={{item}}
                  >{{item}}</span>
                </div>
              </BoxelSelect>
            </div>
          </div>
        </footer>
      </section>
    {{/if}}

    <style scoped>
      .room {
        display: grid;
        grid-template-rows: 1fr auto;
        height: 100%;
        overflow: hidden;
      }
      .skills {
        position: sticky;
        bottom: 0;
        margin-left: auto;
      }
      .unread-indicator {
        position: sticky;
        bottom: 0;
        margin-left: auto;
        width: 100%;
      }
      .unread-button {
        width: 100%;
      }
      .room-actions {
        padding: var(--boxel-sp-xxs) var(--boxel-sp) var(--boxel-sp);
        box-shadow: var(--boxel-box-shadow);
      }
      .chat-input-area {
        background-color: var(--boxel-light);
        border-radius: var(--boxel-border-radius);
        overflow: hidden;
      }
      .chat-input-area__bottom-section {
        display: flex;
        justify-content: space-between;
        gap: 10px;
        align-items: center;
        padding-right: var(--boxel-sp-xxs);
        gap: var(--boxel-sp-xxl);
      }
      .chat-input-area__bottom-section
        :deep(.ember-basic-dropdown-content-wormhole-origin) {
        position: absolute; /* This prevents layout shift when menu opens */
      }
      :deep(.ai-assistant-conversation > *:first-child) {
        margin-top: auto;
      }
      :deep(.ai-assistant-conversation > *:nth-last-of-type(2)) {
        padding-bottom: var(--boxel-sp-xl);
      }
      .supported-llms {
        border: none;
        padding: 0;
        width: auto;
        flex: 1;
        padding-right: var(--boxel-sp-xxs);
        cursor: pointer;
        font:;
      }
      .supported-llms :deep(.boxel-trigger) {
        padding: 0;
      }
      .supported-llms :deep(.boxel-trigger-content) {
        flex: 1;
      }
      .supported-llms[aria-disabled='true'],
      .supported-llms[aria-disabled='true']
        :deep(.boxel-trigger-content .llm-name) {
        background-color: var(--boxel-light);
        color: var(--boxel-500);
      }
      .llm {
        display: grid;
        grid-template-columns: 12px 1fr;
        gap: var(--boxel-sp-xs);
        width: 100%;
      }
      .supported-llms :deep(.boxel-trigger-content .llm) {
        grid-template-columns: 1fr;
      }
      .supported-llms :deep(.boxel-trigger-content .check-mark) {
        display: none;
      }
      .supported-llms :deep(.boxel-trigger-content .llm-name) {
        text-align: right;
      }
      .check-mark {
        height: 12px;
      }
      .llm-name {
        overflow: hidden;
        text-overflow: ellipsis;
        text-wrap: nowrap;
        color: var(--boxel-dark);
        font: 600 var(--boxel-font-sm);
      }
      .loading-indicator {
        width: 100%;
        text-align: right;
      }

      :global(
          .supported-llms__dropdown
            .ember-power-select-option[aria-current='true']
        ) {
        background-color: var(--boxel-teal) !important;
      }
    </style>
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
    @service
    private cardService: CardService;
    @service
    private commandService: CommandService;
    @service
    private matrixService: MatrixService;
    @service
    private operatorModeStateService: OperatorModeStateService;
    private roomResource = getRoom(this, ()=>this.args.roomId, ()=>this.matrixService.getRoomData(this.args.roomId)?.events);
    private autoAttachmentResource = getAutoAttachment(this, ()=>this.topMostStackItems, ()=>this.cardsToAttach);
    @tracked
    private currentMonacoContainer: number | undefined;
    private getConversationScrollability: (() => boolean) | undefined;
    private roomScrollState: WeakMap<RoomData, {
        messageElemements: WeakMap<HTMLElement, number>;
        messageScrollers: Map<number, Element['scrollIntoView']>;
        messageVisibilityObserver: IntersectionObserver;
        isScrolledToBottom: boolean;
        userHasScrolled: boolean;
        isConversationScrollable: boolean;
    }> = new WeakMap();
    constructor(owner1: Owner, args1: Signature['Args']){
        super(owner1, args1);
        this.doMatrixEventFlush.perform();
    }
    private scrollState() {
        if (!this.room) {
            throw new Error(`Cannot get room scroll state before room is loaded`);
        }
        let state1 = this.roomScrollState.get(this.room);
        if (!state1) {
            state1 = new TrackedObject({
                isScrolledToBottom: false,
                userHasScrolled: false,
                isConversationScrollable: false,
                messageElemements: new WeakMap(),
                messageScrollers: new Map(),
                messageVisibilityObserver: new IntersectionObserver((entries1)=>{
                    entries1.forEach((entry1)=>{
                        let index1 = this.messageElements.get(entry1.target as HTMLElement);
                        if (index1 != null) {
                            if ((!this.isConversationScrollable || entry1.isIntersecting) && index1 > this.lastReadMessageIndex) {
                                this.sendReadReceipt(this.messages[index1]);
                            }
                        }
                    });
                })
            });
            this.roomScrollState.set(this.room, state1);
        }
        return state1;
    }
    private get isScrolledToBottom() {
        return this.scrollState().isScrolledToBottom;
    }
    private get userHasScrolled() {
        return this.scrollState().userHasScrolled;
    }
    private get isConversationScrollable() {
        return this.scrollState().isConversationScrollable;
    }
    private get messageElements() {
        return this.scrollState().messageElemements;
    }
    private get messageScrollers() {
        return this.scrollState().messageScrollers;
    }
    private get messageVisibilityObserver() {
        return this.scrollState().messageVisibilityObserver;
    }
    private registerMessageScroller = ({ index: index1, element: element1, scrollTo: scrollTo1 }: {
        index: number;
        element: HTMLElement;
        scrollTo: () => void;
    })=>{
        this.messageElements.set(element1, index1);
        this.messageScrollers.set(index1, scrollTo1);
        this.messageVisibilityObserver.observe(element1);
        this.scrollState().isConversationScrollable = Boolean(this.getConversationScrollability?.());
        if (!this.isConversationScrollable || !this.isAllowedToAutoScroll) {
            return;
        }
        // TODO udpate this so that we preserve the scroll position as the user
        // changes rooms and we restore the scroll position when a user enters a room
        if (// If we are permitted to auto-scroll and if there are no unread messages in the
        // room, then scroll to the last message in the room.
        !this.hasUnreadMessages && index1 === this.messages.length - 1) {
            scrollTo1();
        } else if (// otherwise if we are permitted to auto-scroll and if there are unread
        // messages in the room, then scroll to the first unread message in the room.
        this.hasUnreadMessages && index1 === this.lastReadMessageIndex + 1) {
            scrollTo1();
        }
    };
    private registerConversationScroller = (isConversationScrollable1: () => boolean)=>{
        this.getConversationScrollability = isConversationScrollable1;
    };
    private setScrollPosition = ({ isBottom: isBottom1 }: {
        isBottom: boolean;
    })=>{
        this.scrollState().isScrolledToBottom = isBottom1;
        if (!isBottom1) {
            this.scrollState().userHasScrolled = true;
        }
    };
    private scrollToFirstUnread = ()=>{
        if (!this.hasUnreadMessages) {
            return;
        }
        let firstUnreadIndex1 = this.lastReadMessageIndex + 1;
        let scrollTo1 = this.messageScrollers.get(firstUnreadIndex1);
        if (!scrollTo1) {
            console.warn(`No scroller for message index ${firstUnreadIndex1}`);
        } else {
            scrollTo1({
                behavior: 'smooth'
            });
        }
    };
    private get isAllowedToAutoScroll() {
        return !this.userHasScrolled || this.isScrolledToBottom;
    }
    // For efficiency, read receipts are implemented using “up to” markers. This
    // marker indicates that the acknowledgement applies to all events “up to and
    // including” the event specified. For example, marking an event as “read” would
    // indicate that the user had read all events up to the referenced event.
    @cached
    private get lastReadMessageIndex() {
        let readReceiptIndicies1: number[] = [];
        for (let receipt1 of this.matrixService.currentUserEventReadReceipts.keys()){
            let maybeIndex1 = this.messages.findIndex((m1)=>m1.eventId === receipt1);
            if (maybeIndex1 != null) {
                readReceiptIndicies1.push(maybeIndex1);
            }
        }
        return max(readReceiptIndicies1) ?? -1;
    }
    @cached
    private get numberOfUnreadMessages() {
        let unreadMessagesCount1 = 0;
        let firstUnreadIndex1 = this.lastReadMessageIndex + 1;
        for(let i1 = firstUnreadIndex1; i1 < this.messages.length; i1++){
            if (this.matrixService.profile.userId === this.messages[i1].author.userId) {
                continue;
            }
            unreadMessagesCount1++;
        }
        return unreadMessagesCount1;
    }
    private get hasUnreadMessages() {
        return this.numberOfUnreadMessages > 0;
    }
    private get showUnreadIndicator() {
        // if user is already scrolled to bottom we don't show the indicator to
        // prevent the flash of the indicator appearing and then disappearing during
        // the read receipt acknowledgement
        return (this.isConversationScrollable && this.hasUnreadMessages && !this.isScrolledToBottom);
    }
    private get unreadMessageText() {
        return `${this.numberOfUnreadMessages} unread ${pluralize('message', this.numberOfUnreadMessages)}`;
    }
    private sendReadReceipt(message1: Message) {
        if (this.matrixService.profile.userId === message1.author.userId) {
            return;
        }
        if (this.matrixService.currentUserEventReadReceipts.has(message1.eventId)) {
            return;
        }
        // sendReadReceipt expects an actual MatrixEvent (as defined in the matrix
        // SDK), but it' not available to us here - however, we can fake it by adding
        // the necessary methods
        let matrixEvent1 = {
            getId: ()=>message1.eventId,
            getRoomId: ()=>this.args.roomId,
            getTs: ()=>message1.created.getTime()
        };
        // Without scheduling this after render, this produces the "attempted to
        // update value, but it had already been used previously in the same
        // computation" error
        schedule('afterRender', ()=>{
            this.matrixService.sendReadReceipt(matrixEvent1 as MatrixEvent);
        });
    }
    private maybeRetryAction = (messageIndex1: number, message1: Message)=>{
        if (this.isLastMessage(messageIndex1) && message1.isRetryable) {
            return this.resendLastMessage;
        }
        return undefined;
    };
    private isMessageStreaming = (message1: Message, messageIndex1: number)=>{
        return (!message1.isStreamingFinished && this.isLastMessage(messageIndex1) && // Older events do not come with isStreamingFinished property so we have
        // no other way to determine if the message is done streaming other than
        // checking if they are old messages (older than 60 seconds as an arbitrary
        // threshold)
        unixTime(new Date().getTime() - message1.created.getTime()) < 60);
    };
    private isDisplayingCode = (message1: Message)=>{
        return this.roomResource?.isDisplayingCode(message1);
    };
    private toggleViewCode = (message1: Message)=>{
        this.roomResource.toggleViewCode(message1);
    };
    private doMatrixEventFlush = restartableTask(async ()=>{
        await this.matrixService.flushMembership;
        await this.matrixService.flushTimeline;
        await this.roomResource.loading;
    });
    private get messages() {
        return this.roomResource.messages;
    }
    private get skills(): Skill[] {
        return this.roomResource.skills;
    }
    private get supportedLLMs(): string[] {
        return DEFAULT_LLM_LIST.sort();
    }
    private get sortedSkills(): Skill[] {
        return [
            ...this.skills
        ].sort((a1, b1)=>{
            // Not all of the skills have a title, so we use the skillEventId as a fallback
            // which should be consistent.
            let aTitle1 = a1.card.title || a1.skillEventId;
            let bTitle1 = b1.card.title || b1.skillEventId;
            return aTitle1.localeCompare(bTitle1);
        });
    }
    private get room() {
        let room1 = this.roomResource.matrixRoom;
        return room1;
    }
    private doWhenRoomChanges = restartableTask(async ()=>{
        await all([
            this.cardService.cardsSettled(),
            timeout(500)
        ]);
    });
    private get messageToSend() {
        return this.matrixService.messagesToSend.get(this.args.roomId) ?? '';
    }
    private get cardsToAttach() {
        return this.matrixService.cardsToSend.get(this.args.roomId);
    }
    @action
    private resendLastMessage() {
        if (!this.room) {
            throw new Error('Bug: should not be able to resend a message without a room.');
        }
        let myMessages1 = this.messages.filter((message1)=>message1.author.userId === this.matrixService.userId);
        if (myMessages1.length === 0) {
            throw new Error('Bug: should not be able to resend a message that does not exist.');
        }
        let myLastMessage1 = myMessages1[myMessages1.length - 1];
        let attachedCards1 = (myLastMessage1!.attachedResources || []).map((resource1)=>resource1.card).filter((card1)=>card1 !== undefined) as CardDef[];
        this.doSendMessage.perform(myLastMessage1.message, attachedCards1, true, myLastMessage1.clientGeneratedId);
    }
    @action
    private setMessage(message1: string) {
        this.matrixService.messagesToSend.set(this.args.roomId, message1);
    }
    @action
    private sendMessage(prompt1?: string) {
        let cards1 = [];
        if (this.cardsToAttach) {
            cards1.push(...this.cardsToAttach);
        }
        if (this.autoAttachedCards.size > 0) {
            this.autoAttachedCards.forEach((card1)=>{
                cards1.push(card1);
            });
        }
        this.doSendMessage.perform(prompt1 ?? this.messageToSend, cards1.length ? cards1 : undefined, Boolean(prompt1));
    }
    @action
    private chooseCard(card1: CardDef) {
        let cards1 = this.cardsToAttach ?? [];
        if (!cards1?.find((c1)=>c1.id === card1.id)) {
            this.matrixService.cardsToSend.set(this.args.roomId, [
                ...cards1,
                card1
            ]);
        }
    }
    @action
    private isAutoAttachedCard(card1: CardDef) {
        return this.autoAttachedCards.has(card1);
    }
    @action
    private removeCard(card1: CardDef) {
        if (this.isAutoAttachedCard(card1)) {
            this.autoAttachmentResource.onCardRemoval(card1);
        } else {
            const cardIndex1 = this.cardsToAttach?.findIndex((c1)=>c1.id === card1.id);
            if (cardIndex1 != undefined && cardIndex1 !== -1) {
                if (this.cardsToAttach !== undefined) {
                    this.autoAttachmentResource.onCardRemoval(this.cardsToAttach[cardIndex1]);
                    this.cardsToAttach.splice(cardIndex1, 1);
                }
            }
        }
        this.matrixService.cardsToSend.set(this.args.roomId, this.cardsToAttach?.length ? this.cardsToAttach : undefined);
    }
    private doSendMessage = enqueueTask(async (message1: string | undefined, cards1?: CardDef[], keepInputAndAttachments1: boolean = false, clientGeneratedId1: string = uuidv4())=>{
        if (!keepInputAndAttachments1) {
            // this is for situations when a message is sent via some other way than the text box
            // (example: ai prompt from new-session screen)
            // if there were cards attached or a typed — but not sent — message, do not erase or remove them
            this.matrixService.messagesToSend.set(this.args.roomId, undefined);
            this.matrixService.cardsToSend.set(this.args.roomId, undefined);
        }
        let context1 = {
            submode: this.operatorModeStateService.state.submode,
            openCardIds: this.operatorModeStateService.topMostStackItems().filter((stackItem1)=>stackItem1).map((stackItem1)=>stackItem1.card.id)
        };
        try {
            await this.matrixService.sendMessage(this.args.roomId, message1, cards1, clientGeneratedId1, context1);
        } catch (e1) {
            console.error('Error sending message', e1);
        }
    });
    private get topMostStackItems(): StackItem[] {
        return this.operatorModeStateService.topMostStackItems();
    }
    private get autoAttachedCards() {
        return this.autoAttachmentResource.cards;
    }
    updateSkillIsActiveTask = task(async (skillEventId1: string, isActive1: boolean)=>{
        await new UpdateSkillActivationCommand(this.commandService.commandContext).execute({
            roomId: this.args.roomId,
            skillEventId: skillEventId1,
            isActive: isActive1
        });
    });
    private get canSend() {
        return (!this.doSendMessage.isRunning && Boolean(this.messageToSend?.trim() || this.cardsToAttach?.length || this.autoAttachedCards.size !== 0) && !!this.room && !this.messages.some((m1)=>this.isPendingMessage(m1)));
    }
    @action
    private isLastMessage(messageIndex1: number) {
        return (this.room && messageIndex1 === this.messages.length - 1) ?? false;
    }
    @action
    private setCurrentMonacoContainer(index1: number | undefined) {
        this.currentMonacoContainer = index1;
    }
    private isPendingMessage(message1: Message) {
        return message1.status === 'sending' || message1.status === 'queued';
    }
    private attachSkillTask = task(async (card1: SkillCard)=>{
        let addSkillsToRoomCommand1 = new AddSkillsToRoomCommand(this.commandService.commandContext);
        await addSkillsToRoomCommand1.execute({
            roomId: this.args.roomId,
            skills: [
                card1
            ]
        });
    });
}
declare module '@glint/environment-ember-loose/registry' {
    export default interface Room {
        'Matrix::Room': typeof Room;
    }
}
